import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a blog image gallery with lightbox functionality.
 */
export class BlogImageGallery extends HTMLElement {
  #currentIndex = 0;
  #images = [];
  #dialog = null;
  #dialogImage = null;
  #dialogImageContainer = null;
  #prevButton = null;
  #nextButton = null;
  #counter = null;

  connectedCallback() {

    // Collect image data from gallery items
    const items = this.querySelectorAll('.rte-image-gallery__item');
    this.#images = Array.from(items).map((item) => {
      const img = item.querySelector('img');
      return {
        src: img?.src || '',
        alt: img?.alt || '',
        width: img?.naturalWidth || 0,
        height: img?.naturalHeight || 0,
      };
    });

    // Create lightbox dialog
    this.#createLightbox();

    // Add click handlers to gallery items
    items.forEach((item, index) => {
      item.addEventListener('click', () => this.#openLightbox(index));
    });
  }

  /**
   * Creates the lightbox dialog element
   */
  #createLightbox() {
    const dialog = document.createElement('dialog');
    dialog.classList.add('blog-gallery-lightbox');
    dialog.setAttribute('aria-label', 'Image gallery lightbox');

    dialog.innerHTML = `
      <div class="blog-gallery-lightbox__overlay"></div>
      <div class="blog-gallery-lightbox__content">
        <button type="button" class="blog-gallery-lightbox__close" aria-label="Close gallery">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        
        <div class="blog-gallery-lightbox__image-container">
          <img class="blog-gallery-lightbox__image" alt="" />
        </div>

        ${
          this.#images.length > 1
            ? `
          <button type="button" class="blog-gallery-lightbox__nav blog-gallery-lightbox__nav--prev" aria-label="Previous image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>
          <button type="button" class="blog-gallery-lightbox__nav blog-gallery-lightbox__nav--next" aria-label="Next image">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
          <div class="blog-gallery-lightbox__counter">
            <span class="blog-gallery-lightbox__counter-current">1</span>
            <span class="blog-gallery-lightbox__counter-separator">/</span>
            <span class="blog-gallery-lightbox__counter-total">${this.#images.length}</span>
          </div>
        `
            : ''
        }
      </div>
    `;

    document.body.appendChild(dialog);

    // Store references
    this.#dialog = dialog;
    this.#dialogImage = dialog.querySelector('.blog-gallery-lightbox__image');
    this.#dialogImageContainer = dialog.querySelector('.blog-gallery-lightbox__image-container');
    this.#prevButton = dialog.querySelector('.blog-gallery-lightbox__nav--prev');
    this.#nextButton = dialog.querySelector('.blog-gallery-lightbox__nav--next');
    this.#counter = dialog.querySelector('.blog-gallery-lightbox__counter-current');

    // Add event listeners
    const closeButton = dialog.querySelector('.blog-gallery-lightbox__close');
    closeButton?.addEventListener('click', () => this.#closeLightbox());

    // Close when clicking outside the image
    dialog.addEventListener('click', (e) => {
      if (
        e.target === dialog ||
        e.target.classList.contains('blog-gallery-lightbox__overlay') ||
        e.target.classList.contains('blog-gallery-lightbox__content')
      ) {
        this.#closeLightbox();
      }
    });

    this.#prevButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#navigateLightbox(-1);
    });
    this.#nextButton?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#navigateLightbox(1);
    });

    // Prevent image clicks from closing the dialog
    this.#dialogImageContainer?.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    dialog.addEventListener('keydown', (e) => this.#handleKeydown(e));
    dialog.addEventListener('close', () => this.#onDialogClose());
  }

  /**
   * Opens the lightbox at a specific index
   * @param {number} index - The index of the image to display
   */
  #openLightbox(index) {
    if (!this.#dialog || !this.#dialogImage) return;

    this.#currentIndex = index;
    this.#updateLightboxImage();
    this.#dialog.showModal();
    document.body.style.overflow = 'hidden';
  }

  /**
   * Closes the lightbox
   */
  #closeLightbox() {
    if (!this.#dialog) return;

    this.#dialog.close();
  }

  /**
   * Handles dialog close event
   */
  #onDialogClose() {
    document.body.style.overflow = '';
  }

  /**
   * Navigates to the next or previous image
   * @param {number} direction - 1 for next, -1 for previous
   */
  #navigateLightbox(direction) {
    const newIndex = this.#currentIndex + direction;

    if (newIndex < 0) {
      this.#currentIndex = this.#images.length - 1;
    } else if (newIndex >= this.#images.length) {
      this.#currentIndex = 0;
    } else {
      this.#currentIndex = newIndex;
    }

    this.#updateLightboxImage();
  }

  /**
   * Updates the lightbox image and counter
   */
  #updateLightboxImage() {
    if (!this.#dialogImage || !this.#images[this.#currentIndex]) return;

    const image = this.#images[this.#currentIndex];
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    // Add fade effect
    if (this.#dialogImageContainer) {
      this.#dialogImageContainer.style.opacity = '0';
    }

    // Update image after brief delay for fade effect
    setTimeout(
      () => {
        if (this.#dialogImage) {
          this.#dialogImage.src = image.src;
          this.#dialogImage.alt = image.alt;
        }

        if (this.#counter) {
          this.#counter.textContent = String(this.#currentIndex + 1);
        }

        if (this.#dialogImageContainer) {
          this.#dialogImageContainer.style.opacity = '1';
        }
      },
      behavior === 'instant' ? 0 : 150
    );

    // Update button states
    if (this.#prevButton) {
      this.#prevButton.disabled = false;
    }
    if (this.#nextButton) {
      this.#nextButton.disabled = false;
    }
  }

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#navigateLightbox(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#navigateLightbox(1);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeLightbox();
    }
  }

  disconnectedCallback() {
    // Clean up dialog
    if (this.#dialog) {
      this.#dialog.remove();
    }
  }
}

if (!customElements.get('blog-image-gallery')) {
  customElements.define('blog-image-gallery', BlogImageGallery);
}
